import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReflectionsPage = ({ user }) => {
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    feelings: '',
    progress: '',
    learnings: ''
  });

  useEffect(() => {
    fetchReflections();
  }, []);

  const fetchReflections = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/reflections`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setReflections(response.data);
    } catch (error) {
      console.error('Failed to fetch reflections:', error);
      toast.error('Failed to load reflections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/reflections`,
        { reflections: formData },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      toast.success('Reflection saved!');
      setShowForm(false);
      setFormData({ feelings: '', progress: '', learnings: '' });
      fetchReflections();
    } catch (error) {
      console.error('Failed to save reflection:', error);
      toast.error('Failed to save reflection');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-5xl font-bold text-rose-900" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Weekly Reflections
          </h1>
          <Button
            data-testid="new-reflection-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-rose-500 hover:bg-rose-600"
          >
            <Calendar className="w-4 h-4 mr-2" />
            New Reflection
          </Button>
        </div>

        {showForm && (
          <Card className="shadow-xl border-rose-100 mb-8">
            <CardHeader>
              <CardTitle>This Week's Reflection</CardTitle>
              <CardDescription>Take a moment to reflect on your dating journey</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">How are you feeling about your connections?</label>
                  <Textarea
                    data-testid="feelings-textarea"
                    value={formData.feelings}
                    onChange={(e) => setFormData({ ...formData, feelings: e.target.value })}
                    rows={3}
                    placeholder="Share your thoughts..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">What progress have you made toward your relationship goals?</label>
                  <Textarea
                    data-testid="progress-textarea"
                    value={formData.progress}
                    onChange={(e) => setFormData({ ...formData, progress: e.target.value })}
                    rows={3}
                    placeholder="Reflect on your journey..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">What have you learned about yourself or what you want?</label>
                  <Textarea
                    data-testid="learnings-textarea"
                    value={formData.learnings}
                    onChange={(e) => setFormData({ ...formData, learnings: e.target.value })}
                    rows={3}
                    placeholder="Your learnings..."
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    data-testid="save-reflection-btn"
                    type="submit"
                    className="bg-rose-500 hover:bg-rose-600"
                  >
                    Save Reflection
                  </Button>
                  <Button
                    data-testid="cancel-reflection-btn"
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="border-rose-200"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {reflections.length === 0 ? (
            <Card className="shadow-lg">
              <CardContent className="p-12 text-center">
                <Calendar className="w-16 h-16 text-rose-300 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-rose-900 mb-2">No reflections yet</h3>
                <p className="text-gray-600">Start documenting your dating journey!</p>
              </CardContent>
            </Card>
          ) : (
            reflections.map((reflection) => (
              <Card key={reflection.id} data-testid={`reflection-${reflection.id}`} className="shadow-lg border-rose-100">
                <CardHeader>
                  <CardTitle>
                    Week of {new Date(reflection.week_start).toLocaleDateString()}
                  </CardTitle>
                  <CardDescription>
                    Submitted {new Date(reflection.created_at).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reflection.reflections.feelings && (
                    <div>
                      <h4 className="font-semibold text-rose-900 mb-1">Feelings</h4>
                      <p className="text-gray-700">{reflection.reflections.feelings}</p>
                    </div>
                  )}
                  {reflection.reflections.progress && (
                    <div>
                      <h4 className="font-semibold text-rose-900 mb-1">Progress</h4>
                      <p className="text-gray-700">{reflection.reflections.progress}</p>
                    </div>
                  )}
                  {reflection.reflections.learnings && (
                    <div>
                      <h4 className="font-semibold text-rose-900 mb-1">Learnings</h4>
                      <p className="text-gray-700">{reflection.reflections.learnings}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReflectionsPage;